import { BlockStack, Box, Card, Page, Text, Banner, TextField, Button, InlineStack, DataTable, Modal, Select, Spinner } from "@shopify/polaris";
import { DeleteIcon } from "@shopify/polaris-icons";
import ShippingPricingTable from "../components/ShippingPricingTable";
import { useState, useEffect, useCallback } from "react";
import apiClient from "../utils/api";
import { useMarketplacesList } from "../utils/queries";

export const ShippingAndReturn = () => {
    const [saving, setSaving] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [error, setError] = useState('');

    // Fetch marketplaces dynamically
    const { data: marketplacesData, isLoading: marketplacesLoading } = useMarketplacesList();

    // Global return fee state
    const [globalReturnFee, setGlobalReturnFee] = useState('0.00');
    const [hasGlobalDefault, setHasGlobalDefault] = useState(false);
    const [marketplaceOverrides, setMarketplaceOverrides] = useState({});
    const [returnFeeLoading, setReturnFeeLoading] = useState(true);
    const [returnFeeSaving, setReturnFeeSaving] = useState(false);

    // Modal state for adding marketplace override
    const [showAddMarketplaceModal, setShowAddMarketplaceModal] = useState(false);
    const [newMarketplace, setNewMarketplace] = useState('');
    const [newMarketplaceFee, setNewMarketplaceFee] = useState('0.00');

    // Load global return fee defaults
    const loadGlobalReturnFees = useCallback(async () => {
        try {
            setReturnFeeLoading(true);
            const response = await apiClient.getGlobalReturnFeeDefaults();
            if (response.success) {
                setGlobalReturnFee(response.defaultReturnFee?.toFixed(2) || '0.00');
                setHasGlobalDefault(response.hasGlobalDefault || false);
                setMarketplaceOverrides(response.marketplaceOverrides || {});
            }
        } catch (err) {
            console.error('Error loading global return fees:', err);
            setError('Failed to load global return fee defaults');
        } finally {
            setReturnFeeLoading(false);
        }
    }, []);

    useEffect(() => {
        loadGlobalReturnFees();
    }, [loadGlobalReturnFees]);

    // Save global default return fee
    const saveGlobalReturnFee = async () => {
        try {
            setReturnFeeSaving(true);
            setError('');
            const fee = parseFloat(globalReturnFee);
            if (isNaN(fee) || fee < 0) {
                setError('Return fee must be a non-negative number');
                return;
            }
            const response = await apiClient.updateGlobalReturnFeeDefault(fee);
            if (response.success) {
                setHasGlobalDefault(true);
                await loadGlobalReturnFees();
            }
        } catch (err) {
            console.error('Error saving global return fee:', err);
            setError('Failed to save global return fee');
        } finally {
            setReturnFeeSaving(false);
        }
    };

    // Add marketplace override
    const addMarketplaceOverride = async () => {
        if (!newMarketplace) {
            setError('Please select a marketplace');
            return;
        }
        try {
            setReturnFeeSaving(true);
            setError('');
            const fee = parseFloat(newMarketplaceFee);
            if (isNaN(fee) || fee < 0) {
                setError('Return fee must be a non-negative number');
                return;
            }
            const response = await apiClient.createGlobalMarketplaceReturnFee(newMarketplace, fee);
            if (response.success) {
                setShowAddMarketplaceModal(false);
                setNewMarketplace('');
                setNewMarketplaceFee('0.00');
                await loadGlobalReturnFees();
            }
        } catch (err) {
            console.error('Error adding marketplace override:', err);
            setError(err.message || 'Failed to add marketplace override');
        } finally {
            setReturnFeeSaving(false);
        }
    };

    // Delete marketplace override
    const deleteMarketplaceOverride = async (marketplace) => {
        if (!confirm(`Are you sure you want to delete the return fee override for "${marketplace}"?`)) {
            return;
        }
        try {
            setReturnFeeSaving(true);
            setError('');
            const response = await apiClient.deleteGlobalMarketplaceReturnFee(marketplace);
            if (response.success) {
                await loadGlobalReturnFees();
            }
        } catch (err) {
            console.error('Error deleting marketplace override:', err);
            setError('Failed to delete marketplace override');
        } finally {
            setReturnFeeSaving(false);
        }
    };

    // Build marketplace override rows for DataTable
    const marketplaceRows = Object.entries(marketplaceOverrides).map(([marketplace, fee]) => [
        marketplace,
        `$${fee.toFixed(2)}`,
        <Button
            key={marketplace}
            icon={DeleteIcon}
            tone="critical"
            size="micro"
            onClick={() => deleteMarketplaceOverride(marketplace)}
            disabled={returnFeeSaving}
        >
            Remove
        </Button>
    ]);

    // Build marketplace options dynamically from API, filter out already-configured ones
    const availableMarketplaceOptions = [
        { label: 'Select marketplace...', value: '' },
        ...(marketplacesData?.marketplaces || [])
            .filter(mp => !marketplaceOverrides[mp])
            .map(mp => ({ label: mp, value: mp }))
    ];

    // Weight range options matching ShippingPricingTable
    const weightRangeOptions = [
        // 0-10 lb: 1-lb intervals
        { value: '0-1', minWeight: 0, maxWeight: 1 },
        { value: '1-2', minWeight: 1, maxWeight: 2 },
        { value: '2-3', minWeight: 2, maxWeight: 3 },
        { value: '3-4', minWeight: 3, maxWeight: 4 },
        { value: '4-5', minWeight: 4, maxWeight: 5 },
        { value: '5-6', minWeight: 5, maxWeight: 6 },
        { value: '6-7', minWeight: 6, maxWeight: 7 },
        { value: '7-8', minWeight: 7, maxWeight: 8 },
        { value: '8-9', minWeight: 8, maxWeight: 9 },
        { value: '9-10', minWeight: 9, maxWeight: 10 },
        // 10-20 lb: 2-lb intervals
        { value: '10-12', minWeight: 10, maxWeight: 12 },
        { value: '12-14', minWeight: 12, maxWeight: 14 },
        { value: '14-16', minWeight: 14, maxWeight: 16 },
        { value: '16-18', minWeight: 16, maxWeight: 18 },
        { value: '18-20', minWeight: 18, maxWeight: 20 },
        // 20-50 lb: 5-lb intervals
        { value: '20-25', minWeight: 20, maxWeight: 25 },
        { value: '25-30', minWeight: 25, maxWeight: 30 },
        { value: '30-35', minWeight: 30, maxWeight: 35 },
        { value: '35-40', minWeight: 35, maxWeight: 40 },
        { value: '40-45', minWeight: 40, maxWeight: 45 },
        { value: '45-50', minWeight: 45, maxWeight: 50 },
    ];

    const handleDefaultShippingPricingSave = async (changes, fullPricingData) => {
        if (!changes || changes.length === 0) return;

        try {
            setSaving(true);
            setError('');

            // Transform changes into the format expected by the PATCH API
            const updates = changes.map(change => {
                const weightRange = weightRangeOptions.find(r => r.value === change.weightRangeId);
                return {
                    shippingMethod: change.serviceId,
                    weightMin: weightRange?.minWeight,
                    weightMax: weightRange?.maxWeight,
                    fee: change.newValue ? parseFloat(change.newValue) : null, // null = delete/reset
                    oldFee: change.oldValue ? parseFloat(change.oldValue) : null // for audit
                };
            });

            // Use PATCH endpoint for granular updates
            const response = await apiClient.request(`/brands/default/shipping-fees`, {
                method: 'PATCH',
                body: JSON.stringify({ updates }),
            });

            if (!response.success) {
                throw new Error(response.message || 'Failed to save shipping pricing');
            }
        } catch (err) {
            console.error('Error saving shipping pricing:', err);
            setError(`Failed to save shipping pricing: ${err.message}`);
            throw err; // Re-throw so the component can handle the error
        } finally {
            setSaving(false);
        }
    };

    return (
        <Page
            title="Shipping & Return"
            subtitle="Manage the default shipping and return rates applied across all brands."
            fullWidth
        >
            <BlockStack gap="400">
                {/* Error banner */}
                {error && (
                    <Banner tone="critical" onDismiss={() => setError('')}>
                        {error}
                    </Banner>
                )}

                {/* Shipping Pricing Section */}
                <Card>
                    <Box padding="600">
                        <BlockStack gap="400">
                            <BlockStack gap="200">
                                <Text variant="headingMd" as="h3">BoxC Shipping Pricing</Text>
                                <Text variant="bodyMd" tone="subdued">
                                    Configure default pricing for BoxC services across different weight ranges. These rates apply to all brands unless overridden.
                                </Text>
                            </BlockStack>
                            <ShippingPricingTable
                                brand={null}
                                onSave={handleDefaultShippingPricingSave}
                                loading={saving}
                                disabled={isSyncing}
                            />
                        </BlockStack>
                    </Box>
                </Card>

                {/* Return Fees Section */}
                <Card>
                    <Box padding="600">
                        <BlockStack gap="400">
                            <BlockStack gap="200">
                                <Text variant="headingMd" as="h3">Global Return Fee Defaults</Text>
                                <Text variant="bodyMd" tone="subdued">
                                    Configure default return fees applied when processing refunds. Brands can override these values with their own settings.
                                </Text>
                            </BlockStack>

                            {/* Default Return Fee */}
                            <Card background="bg-surface-secondary">
                                <Box padding="400">
                                    <BlockStack gap="300">
                                        <Text variant="headingSm" as="h4">Default Return Fee</Text>
                                        <InlineStack gap="300" align="start" blockAlign="end">
                                            <div style={{ width: '200px' }}>
                                                <TextField
                                                    label="Fee amount"
                                                    type="number"
                                                    prefix="$"
                                                    value={globalReturnFee}
                                                    onChange={setGlobalReturnFee}
                                                    min={0}
                                                    step={0.01}
                                                    disabled={returnFeeLoading || returnFeeSaving}
                                                    autoComplete="off"
                                                />
                                            </div>
                                            <Button
                                                variant="primary"
                                                onClick={saveGlobalReturnFee}
                                                loading={returnFeeSaving}
                                                disabled={returnFeeLoading}
                                            >
                                                {hasGlobalDefault ? 'Update Default' : 'Set Default'}
                                            </Button>
                                        </InlineStack>
                                        {hasGlobalDefault && (
                                            <Text variant="bodySm" tone="subdued">
                                                Current default: ${parseFloat(globalReturnFee).toFixed(2)} - This applies to all brands unless they have their own override.
                                            </Text>
                                        )}
                                    </BlockStack>
                                </Box>
                            </Card>

                            {/* Marketplace Overrides */}
                            <Card background="bg-surface-secondary">
                                <Box padding="400">
                                    <BlockStack gap="300">
                                        <InlineStack align="space-between">
                                            <Text variant="headingSm" as="h4">Marketplace Overrides</Text>
                                            <Button
                                                onClick={() => setShowAddMarketplaceModal(true)}
                                                disabled={returnFeeSaving || marketplacesLoading || availableMarketplaceOptions.length <= 1}
                                                loading={marketplacesLoading}
                                            >
                                                Add Marketplace Override
                                            </Button>
                                        </InlineStack>
                                        <Text variant="bodySm" tone="subdued">
                                            Set different return fees for specific marketplaces. These override the default return fee globally.
                                            {!marketplacesLoading && availableMarketplaceOptions.length <= 1 && Object.keys(marketplaceOverrides).length > 0 && (
                                                <> All marketplaces have been configured.</>
                                            )}
                                        </Text>

                                        {marketplaceRows.length > 0 ? (
                                            <DataTable
                                                columnContentTypes={['text', 'numeric', 'text']}
                                                headings={['Marketplace', 'Return Fee', 'Actions']}
                                                rows={marketplaceRows}
                                            />
                                        ) : (
                                            <Box paddingBlock="400">
                                                <Text tone="subdued" alignment="center">
                                                    No marketplace overrides configured. All marketplaces use the default return fee
                                                    {hasGlobalDefault ? ` of $${parseFloat(globalReturnFee).toFixed(2)}` : ''}.
                                                </Text>
                                            </Box>
                                        )}
                                    </BlockStack>
                                </Box>
                            </Card>
                        </BlockStack>
                    </Box>
                </Card>
            </BlockStack>

            {/* Add Marketplace Override Modal */}
            <Modal
                open={showAddMarketplaceModal}
                onClose={() => {
                    setShowAddMarketplaceModal(false);
                    setNewMarketplace('');
                    setNewMarketplaceFee('0.00');
                }}
                title="Add Marketplace Return Fee Override"
                primaryAction={{
                    content: 'Add Override',
                    onAction: addMarketplaceOverride,
                    loading: returnFeeSaving,
                    disabled: !newMarketplace,
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: () => {
                            setShowAddMarketplaceModal(false);
                            setNewMarketplace('');
                            setNewMarketplaceFee('0.00');
                        },
                    },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        <Select
                            label="Marketplace"
                            options={availableMarketplaceOptions}
                            value={newMarketplace}
                            onChange={setNewMarketplace}
                            disabled={marketplacesLoading}
                            helpText={marketplacesLoading ? "Loading marketplaces..." : undefined}
                        />
                        <TextField
                            label="Return Fee"
                            type="number"
                            prefix="$"
                            value={newMarketplaceFee}
                            onChange={setNewMarketplaceFee}
                            min={0}
                            step={0.01}
                            helpText={hasGlobalDefault ? `Current global default: $${parseFloat(globalReturnFee).toFixed(2)}` : 'No global default set'}
                            autoComplete="off"
                        />
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </Page>
    )
}